import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import ArticleCarousel from '../../../components/ArticleCarousel';

const CATEGORY_NAME_TO_COLOR = {
    'frankly news': '#6d1b1b',
    'habbo news': '#c48a00',
    'chickas charity': '#c86aa0',
    'rare values': '#4f9ea4',
    'frankly guides': '#6d1b1b',
    'guides': '#2d6a1e',
    'competitions': '#6c2d82',
};

const CATEGORY_NAME_TO_SPOTLIGHT_COLOR = {
    'frankly news': 4,
    'habbo news': 1,
    'chickas charity': 5,
    'rare values': 3,
    'frankly guides': 4,
    'guides': 2,
    'competitions': 6,
};

function ArticleCard({ article, onToggle, toggling }) {
    const catColor = CATEGORY_NAME_TO_COLOR[article.categoryName?.toLowerCase()] || '#6b7280';

    return (
        <div
            className={`bg-white border-2 overflow-hidden flex flex-col ${article.spotlight ? 'border-yellow-500' : 'border-gray-300'}`}
            style={{ boxShadow: article.spotlight ? '0 3px 0 0 rgba(200,150,0,0.3)' : '0 2px 0 0 rgba(0,0,0,0.1)' }}
        >
            <div className="overflow-hidden" style={{ height: '140px' }}>
                {article.headerImage ? (
                    <img
                        src={article.headerImage}
                        alt=""
                        style={{ width: '100%', height: 'auto', maxWidth: 'none', display: 'block' }}
                    />
                ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs uppercase">No Image</span>
                    </div>
                )}
            </div>

            <div className="p-3 flex flex-col flex-1">
                <div className="flex items-start gap-2 mb-1">
                    <h3 className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-900 leading-tight flex-1">
                        {article.title}
                    </h3>
                    {article.spotlight && (
                        <span className="text-[10px] font-bold uppercase text-yellow-600 shrink-0 mt-0.5">
                            ★ Live
                        </span>
                    )}
                </div>

                <p className="text-[12px] text-gray-500 mb-3 line-clamp-3 flex-1">
                    {article.excerpt || 'No preview text available.'}
                </p>

                <div className="flex items-center justify-between gap-2 mt-auto">
                    <span
                        className="text-[11px] uppercase font-bold px-2 py-0.5 text-white"
                        style={{ backgroundColor: catColor, boxShadow: '0 1px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        {article.categoryName}
                    </span>

                    <button
                        onClick={() => onToggle(article)}
                        disabled={toggling === article.id}
                        className={`text-[12px] uppercase px-3 py-1 transition-opacity hover:opacity-80 disabled:opacity-50 ${
                            article.spotlight ? 'bg-gray-400 text-white' : 'bg-maroon text-white'
                        }`}
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        {toggling === article.id ? '...' : article.spotlight ? 'Unspotlight' : 'Spotlight'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function SpotlightArticles({ userData }) {
    const [spotlighted, setSpotlighted] = useState([]);
    const [articles, setArticles] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const [toggling, setToggling] = useState(null);
    const apiUrl = process.env.REACT_APP_API_URL;

    const loadPage = useCallback(async (page, isInitial = false) => {
        isInitial ? setLoading(true) : setPageLoading(true);
        try {
            const res = await fetch(`${apiUrl}admin/articles?page=${page}`, { credentials: 'include' });
            const data = await res.json();
            if (data.success) {
                setSpotlighted(data.spotlight);
                setArticles(data.articles);
                setPagination(data.pagination);
            } else {
                toast.error('Failed to load articles');
            }
        } catch {
            toast.error('Failed to load articles');
        } finally {
            isInitial ? setLoading(false) : setPageLoading(false);
        }
    }, [apiUrl]);

    useEffect(() => {
        loadPage(1, true);
    }, [loadPage]);

    const handleToggle = async (article) => {
        const newSpotlight = !article.spotlight;
        const spotlightColorId = CATEGORY_NAME_TO_SPOTLIGHT_COLOR[article.categoryName?.toLowerCase()] ?? 4;

        setToggling(article.id);
        try {
            const res = await fetch(`${apiUrl}admin/articles/${article.id}/spotlight`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    spotlight: newSpotlight,
                    spotlightColorId: newSpotlight ? spotlightColorId : null
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(newSpotlight ? 'Article added to spotlight' : 'Article removed from spotlight');
                await loadPage(pagination.page);
            } else {
                toast.error(data.message || 'Failed to update spotlight');
            }
        } catch {
            toast.error('Failed to update spotlight');
        } finally {
            setToggling(null);
        }
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const carouselArticles = spotlighted.map((a, i) => ({
        ...a,
        body: a.excerpt,
        sortOrder: i
    }));

    return (
        <AdminLayout>
            <div className="space-y-4">
                {/* Live carousel preview */}
                <ContentBox theme="default" tint="red" header="Carousel Preview">
                    {loading ? (
                        <div className="h-[260px] bg-gray-100 animate-pulse" />
                    ) : (
                        <ArticleCarousel articles={carouselArticles} />
                    )}
                </ContentBox>

                <ContentBox theme="default" tint="red" header="Spotlight Articles">
                    {loading ? (
                        <p className="text-sm uppercase">Loading articles...</p>
                    ) : (
                        <div className="space-y-6">
                            {/* Spotlighted */}
                            <div>
                                <p className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] mb-3 text-maroon">
                                    In Spotlight ({spotlighted.length})
                                </p>
                                {spotlighted.length === 0 ? (
                                    <p className="text-sm text-gray-500 uppercase">No articles currently in spotlight</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {spotlighted.map(article => (
                                            <ArticleCard
                                                key={article.id}
                                                article={article}
                                                onToggle={handleToggle}
                                                toggling={toggling}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            <hr className="border-gray-300" />

                            {/* Paginated non-spotlight articles */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-700">
                                        All Articles ({pagination.total})
                                    </p>
                                    {pagination.totalPages > 1 && (
                                        <span className="text-[12px] text-gray-500 uppercase">
                                            Page {pagination.page} of {pagination.totalPages}
                                        </span>
                                    )}
                                </div>

                                {pageLoading ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {Array.from({ length: 6 }).map((_, i) => (
                                            <div key={i} className="h-[260px] bg-gray-100 animate-pulse border border-gray-200" />
                                        ))}
                                    </div>
                                ) : articles.length === 0 ? (
                                    <p className="text-sm text-gray-500 uppercase">All articles are spotlighted</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {articles.map(article => (
                                            <ArticleCard
                                                key={article.id}
                                                article={article}
                                                onToggle={handleToggle}
                                                toggling={toggling}
                                            />
                                        ))}
                                    </div>
                                )}

                                {pagination.totalPages > 1 && (
                                    <div className="flex items-center justify-center gap-2 mt-4">
                                        <button
                                            onClick={() => loadPage(pagination.page - 1)}
                                            disabled={pagination.page <= 1 || pageLoading}
                                            className="bg-gray-200 text-gray-800 px-4 py-2 text-[12px] uppercase hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.15)' }}
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => loadPage(pagination.page + 1)}
                                            disabled={pagination.page >= pagination.totalPages || pageLoading}
                                            className="bg-maroon text-white px-4 py-2 text-[12px] uppercase hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </ContentBox>
            </div>
        </AdminLayout>
    );
}

export default SpotlightArticles;
